import { useState, useEffect, useCallback, useRef } from 'react';
import { productsApi, assetsApi } from '../api/products.api';
import type { Product, UpdateAssetSettingsRequest, GenerateAssetRequest } from '../types/product.types';

export function useProduct(productId: string) {
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const fetchProduct = useCallback(async (silent = false) => {
    if (!productId) return;
    try {
      if (!silent) {
        setLoading(true);
        setError(null);
      }
      const data = await productsApi.get(productId);
      setProduct(data);
    } catch (err) {
      if (!silent) {
        setError(err instanceof Error ? err : new Error('Failed to fetch product'));
      }
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [productId]);

  useEffect(() => {
    fetchProduct();
  }, [fetchProduct]);

  // Poll for asset generation status
  useEffect(() => {
    // Check if asset is processing or pending
    const isProcessing = product?.asset?.status === 'processing' || product?.asset?.status === 'pending';

    if (isProcessing) {
      // Poll every 3 seconds for status updates (silent refresh to avoid flickering)
      pollingIntervalRef.current = setInterval(() => {
        fetchProduct(true); // silent = true to avoid setting loading state
      }, 3000);
    } else {
      // Clear polling if asset is not processing
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    }

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, [product?.asset?.status, fetchProduct]);

  const updateSettings = useCallback(async (request: UpdateAssetSettingsRequest) => {
    if (!productId) return;
    try {
      await assetsApi.updateSettings(productId, request);
      // Silent refresh keeps loading=false so the form doesn't unmount/remount.
      await fetchProduct(true);
    } catch (err) {
      throw err instanceof Error ? err : new Error('Failed to update settings');
    }
  }, [productId, fetchProduct]);

  const generateAsset = useCallback(async (request: GenerateAssetRequest = {}) => {
    if (!productId) return;
    try {
      await assetsApi.generate(productId, request);
      // Refresh product after generation to get the new asset status
      // Polling will automatically start if status is 'processing' or 'pending'
      await fetchProduct(false);
    } catch (err) {
      throw err instanceof Error ? err : new Error('Failed to generate asset');
    }
  }, [productId, fetchProduct]);

  const cancelGeneration = useCallback(async () => {
    if (!productId) return;
    try {
      await assetsApi.cancelGeneration(productId);
      // Refresh product after cancellation
      await fetchProduct();
    } catch (err) {
      throw err instanceof Error ? err : new Error('Failed to cancel generation');
    }
  }, [productId, fetchProduct]);

  const deleteAsset = useCallback(async () => {
    if (!productId) return;
    try {
      await assetsApi.delete(productId);
      // Refresh product after deletion
      await fetchProduct();
    } catch (err) {
      throw err instanceof Error ? err : new Error('Failed to delete asset');
    }
  }, [productId, fetchProduct]);

  return {
    product,
    loading,
    error,
    refetch: fetchProduct,
    updateSettings,
    generateAsset,
    cancelGeneration,
    deleteAsset,
  };
}

